/**
 * MobileMissionsList
 *
 * Mobile card-based replacement for MissionsDataTable.
 * Shows each mission as a touch-friendly card with key info.
 * Supports search, pagination, and tap-to-view details.
 *
 * Enhanced with futuristic glassmorphic aesthetic, staggered animations,
 * color-coded date chips, and micro-interactions.
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { getTagClass } from "../../../app/utils/helpers";
import { formatDate } from "../contractsTableUtils";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_SPRING = "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)";
const PAGE_SIZE = 20;

/* Glassmorphism layers */
const GLASS_CARD = {
  bgcolor: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(255,255,255,0.45)",
  boxShadow:
    "0 4px 24px rgba(0,40,128,0.04), 0 1.5px 6px rgba(0,40,128,0.03), inset 0 1px 0 rgba(255,255,255,0.6)",
};

const GLASS_SEARCH = {
  bgcolor: "rgba(255,255,255,0.7)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255,255,255,0.5)",
  boxShadow: "0 2px 12px rgba(0,40,128,0.04), inset 0 1px 0 rgba(255,255,255,0.5)",
};

/**
 * @param {Object} props
 * @param {Array}  props.data - allMissionsList
 * @param {Function} props.onCardTap - called with mission data when tapped
 * @param {Function} props.onValueChange - called with filtered data for export
 * @param {Function} props.onTeamTap - navigate to team page
 */
export default function MobileMissionsList({
  data,
  onCardTap,
  onValueChange,
  onTeamTap,
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  const baseData = data || [];

  // Filter by search
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return baseData;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return baseData.filter((item) => {
      const searchStr = [
        item.nom,
        item.prenom,
        item.statut,
        item.client,
        item.current_team_name,
        item.poste_name,
        item.group_name,
        item.current_team_location,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => searchStr.includes(t));
    });
  }, [baseData, searchText]);

  // Notify parent
  const notifyParent = useCallback(() => {
    if (onValueChange) onValueChange(filteredData);
  }, [filteredData, onValueChange]);

  useEffect(() => {
    notifyParent();
  }, [notifyParent]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const getStatusColor = (statut) => {
    if (["Présent", "Actif"].includes(statut)) return "success";
    if (statut === "Parti") return "error";
    return "default";
  };

  const isDateFuture = (dateStr) =>
    dateStr && moment(dateStr).isAfter(moment());
  const isDatePast = (dateStr) =>
    dateStr && moment(dateStr).isBefore(moment());

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: "130px",
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Search bar ── */}
      <TextField
        fullWidth
        size="small"
        placeholder={Localize("Search") + "..."}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                fontSize="small"
                sx={{ color: "rgba(0,40,128,0.4)", transition: TRANSITION_SMOOTH }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& input": {
            fontSize: "16px",
            fontWeight: 400,
            letterSpacing: "0.01em",
            "&::placeholder": { opacity: 0.5 },
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: "16px",
            ...GLASS_SEARCH,
            transition: TRANSITION_SMOOTH,
            "& fieldset": {
              borderColor: "rgba(0,40,128,0.06)",
              transition: TRANSITION_SMOOTH,
            },
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.85)",
              "& fieldset": { borderColor: "rgba(255,144,21,0.25)" },
            },
            "&.Mui-focused": {
              bgcolor: "rgba(255,255,255,0.92)",
              boxShadow: "0 0 0 3px rgba(255,144,21,0.12), 0 4px 16px rgba(0,40,128,0.06)",
              "& fieldset": { borderColor: "rgba(255,144,21,0.5)" },
            },
            "&.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root": {
              color: "rgb(255,144,21)",
            },
          },
        }}
      />

      {/* ── Results count ── */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 500,
          fontSize: "0.7rem",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          opacity: 0.55,
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* ── Card list ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,40,128,0.1)",
            borderRadius: 3,
            "&:hover": { background: "rgba(0,40,128,0.18)" },
          },
        }}
      >
        {paginatedData.map((item, index) => (
          <Fade
            in
            key={item.id}
            timeout={300 + index * 50}
            style={{ transitionDelay: `${index * 30}ms` }}
          >
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "18px",
                overflow: "hidden",
                ...GLASS_CARD,
                transition:
                  "transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1), border-color 250ms ease",
                "&:hover": {
                  boxShadow:
                    "0 8px 32px rgba(0,40,128,0.08), 0 2px 8px rgba(0,40,128,0.05)",
                  borderColor: "rgba(255,144,21,0.15)",
                },
                "&:active": {
                  transform: "scale(0.975)",
                  boxShadow:
                    "0 2px 8px rgba(0,40,128,0.04), 0 1px 3px rgba(0,40,128,0.03)",
                },
              }}
            >
              <CardActionArea
                onClick={() => onCardTap(item)}
                sx={{
                  p: 0,
                  "& .MuiCardActionArea-focusHighlight": {
                    background: "rgba(255,144,21,0.04)",
                  },
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 1.75 } }}>
                  {/* ── Top row: avatar + name + status ── */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {/* Avatar with gradient ring */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, rgba(255,144,21,0.9), rgba(255,193,0,0.85))",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "0.82rem",
                          letterSpacing: "0.02em",
                          flexShrink: 0,
                          boxShadow:
                            "0 3px 12px rgba(255,144,21,0.25), inset 0 1px 0 rgba(255,255,255,0.25)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      >
                        {(item.prenom?.[0] || "").toUpperCase()}
                        {(item.nom?.[0] || "").toUpperCase()}
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 650,
                            fontSize: "0.92rem",
                            lineHeight: 1.3,
                            letterSpacing: "-0.015em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "rgba(0,20,60,0.88)",
                          }}
                        >
                          {item.prenom} {item.nom}
                        </Typography>
                        {item.client && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.73rem",
                              color: "rgba(0,40,128,0.5)",
                              fontWeight: 450,
                              letterSpacing: "0.005em",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.client}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status chip with soft glow */}
                    {item.statut && (
                      <Chip
                        label={item.statut}
                        size="small"
                        color={getStatusColor(item.statut)}
                        variant={
                          ["Présent", "Actif"].includes(item.statut)
                            ? "filled"
                            : "outlined"
                        }
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          height: 24,
                          borderRadius: "8px",
                          letterSpacing: "0.02em",
                          ml: 1,
                          flexShrink: 0,
                          transition: TRANSITION_SMOOTH,
                          ...(["Présent", "Actif"].includes(item.statut) && {
                            boxShadow: "0 2px 8px rgba(58,156,96,0.25)",
                          }),
                          ...(item.statut === "Parti" && {
                            boxShadow: "0 2px 8px rgba(251,105,98,0.2)",
                          }),
                        }}
                      />
                    )}
                  </Box>

                  {/* ── Info chips row ── */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.6,
                      mt: 0.25,
                      mb: 0.75,
                    }}
                  >
                    {item.current_team_name && (
                      <Chip
                        label={item.current_team_name}
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onTeamTap) onTeamTap(item);
                        }}
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 550,
                          height: 26,
                          maxWidth: 170,
                          borderRadius: "9px",
                          transition: TRANSITION_SPRING,
                          cursor: "pointer",
                          color: "rgb(0,40,128)",
                          borderColor: "rgba(0,40,128,0.2)",
                          bgcolor: "rgba(0,40,128,0.04)",
                          "&:hover": {
                            bgcolor: "rgba(0,40,128,0.1)",
                            borderColor: "rgba(0,40,128,0.35)",
                            transform: "scale(1.03)",
                            boxShadow: "0 2px 8px rgba(0,40,128,0.1)",
                          },
                          "&:active": {
                            transform: "scale(0.97)",
                          },
                        }}
                      />
                    )}
                    {item.poste_name && (
                      <Chip
                        label={item.poste_name}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 450,
                          height: 26,
                          maxWidth: 170,
                          borderRadius: "9px",
                          borderColor: "rgba(0,0,0,0.08)",
                          bgcolor: "rgba(0,0,0,0.02)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                    {item.group_name && (
                      <Chip
                        label={item.group_name}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 450,
                          height: 26,
                          maxWidth: 170,
                          borderRadius: "9px",
                          borderColor: "rgba(0,0,0,0.08)",
                          bgcolor: "rgba(0,0,0,0.02)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                  </Box>

                  {/* ── Thin divider ── */}
                  <Divider
                    sx={{
                      my: 0.75,
                      borderColor: "rgba(0,40,128,0.04)",
                      opacity: 0.8,
                    }}
                  />

                  {/* ── Date chips row ── */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.75,
                      mt: 0.5,
                      alignItems: "center",
                    }}
                  >
                    {item.start_date && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.35,
                          borderRadius: "8px",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          letterSpacing: "0.01em",
                          transition: TRANSITION_SMOOTH,
                          ...(isDateFuture(item.start_date)
                            ? {
                                color: "rgb(38,126,76)",
                                bgcolor: "rgba(58,156,96,0.08)",
                                border: "1px solid rgba(58,156,96,0.15)",
                              }
                            : {
                                color: "rgba(0,20,60,0.55)",
                                bgcolor: "rgba(0,0,0,0.025)",
                                border: "1px solid rgba(0,0,0,0.05)",
                              }),
                        }}
                      >
                        <CalendarTodayOutlinedIcon
                          sx={{ fontSize: "0.8rem", opacity: 0.7 }}
                        />
                        <span>
                          <Langs str="start_date" />
                        </span>
                        <strong>{formatDate(item.start_date)}</strong>
                      </Box>
                    )}
                    {item.end_date && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.35,
                          borderRadius: "8px",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          letterSpacing: "0.01em",
                          transition: TRANSITION_SMOOTH,
                          ...(isDatePast(item.end_date)
                            ? {
                                color: "rgb(191,65,58)",
                                bgcolor: "rgba(251,105,98,0.07)",
                                border: "1px solid rgba(251,105,98,0.15)",
                              }
                            : {
                                color: "rgba(0,20,60,0.55)",
                                bgcolor: "rgba(0,0,0,0.025)",
                                border: "1px solid rgba(0,0,0,0.05)",
                              }),
                        }}
                      >
                        <EventBusyOutlinedIcon
                          sx={{ fontSize: "0.8rem", opacity: 0.7 }}
                        />
                        <span>
                          <Langs str="end_date" />
                        </span>
                        <strong>{formatDate(item.end_date)}</strong>
                      </Box>
                    )}
                  </Box>

                  {/* ── Location row ── */}
                  {item.current_team_location && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.75,
                      }}
                    >
                      <PlaceOutlinedIcon
                        sx={{
                          fontSize: "0.82rem",
                          color: "rgba(0,40,128,0.35)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.72rem",
                          color: "rgba(0,40,128,0.5)",
                          fontWeight: 450,
                          letterSpacing: "0.005em",
                        }}
                      >
                        {item.current_team_location}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}

        {/* ── Empty state ── */}
        {paginatedData.length === 0 && (
          <Fade in timeout={500}>
            <Box
              sx={{
                textAlign: "center",
                py: 10,
                px: 3,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,144,21,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <SearchIcon
                  sx={{ fontSize: "1.5rem", color: "rgba(255,144,21,0.4)" }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(0,20,60,0.35)",
                  fontWeight: 450,
                  fontSize: "0.85rem",
                }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
